// qa-app/src/ai-coding-outputs/useEvidenceHighlighting.ts
import { useCallback, useMemo, useState } from "react";
import type { EvidenceHighlight } from "@/components/ui/document-viewer";
import type {
  DiagnosisAction,
  DiagnosisEvidenceRow,
  EvidenceHighlightState,
  TimelinePanelState,
} from "./human-coding/types";
import { UNMATCHED_PREFIX, buildAllHighlights, countTimelineQuotes, findQuoteIndex } from "./evidenceUtils";
import type { UnmatchedLeadTerm } from "./human-coding/useUnmatchedLeadTerms";

export interface UseEvidenceHighlightingReturn {
  timelinePanel: TimelinePanelState;
  allHighlights: EvidenceHighlight[];
  activeFileIdentifier: string | undefined;
  scrollTrigger: number;
  evidenceByCode: Map<string, { description: string; timeline: DiagnosisAction[] }>;
  evidenceHighlight: EvidenceHighlightState;
  handleOpenTimeline: (code: string) => void;
  handleCloseTimeline: () => void;
  handleToggleHighlight: (code: string) => void;
  handleTimelineEvidenceClick: (quote: { text: string; fileIdentifier: string }) => void;
}

export function useEvidenceHighlighting(
  evidenceData: DiagnosisEvidenceRow[] | undefined,
  unmatchedLeadTerms?: UnmatchedLeadTerm[],
): UseEvidenceHighlightingReturn {
  const [timelinePanel, setTimelinePanel] = useState<TimelinePanelState>({ activeCode: null });
  const [evidenceHighlight, setEvidenceHighlight] = useState<EvidenceHighlightState>({
    focusedCode: null,
    focusedQuoteIndex: 0,
  });
  const [scrollTrigger, setScrollTrigger] = useState(0);

  const evidenceByCode = useMemo(() => {
    const map = new Map<string, { description: string; timeline: DiagnosisAction[] }>();
    if (evidenceData) {
      for (const row of evidenceData) {
        map.set(row.icd10Code, { description: row.description, timeline: row.timeline });
      }
    }
    return map;
  }, [evidenceData]);

  const allHighlights = useMemo(
    () =>
      buildAllHighlights(
        evidenceData ?? [],
        evidenceHighlight.focusedCode,
        evidenceHighlight.focusedQuoteIndex,
        unmatchedLeadTerms,
      ),
    [evidenceData, evidenceHighlight.focusedCode, evidenceHighlight.focusedQuoteIndex, unmatchedLeadTerms],
  );

  const activeFileIdentifier = useMemo(() => {
    const activeQuote = allHighlights.find((hl) => hl.isActiveQuote);
    return activeQuote?.fileIdentifier;
  }, [allHighlights]);

  const handleOpenTimeline = useCallback((code: string) => {
    setTimelinePanel((prev) => ({
      activeCode: prev.activeCode === code ? null : code,
    }));
  }, []);

  const handleCloseTimeline = useCallback(() => {
    setTimelinePanel({ activeCode: null });
  }, []);

  const handleToggleHighlight = useCallback(
    (code: string) => {
      setEvidenceHighlight((prev) => {
        if (prev.focusedCode === code) {
          // Count total quotes for this code
          let totalQuotes = 0;
          if (code.startsWith(UNMATCHED_PREFIX)) {
            const leadTerm = code.slice(UNMATCHED_PREFIX.length);
            const term = unmatchedLeadTerms?.find((t) => t.leadTerm === leadTerm);
            totalQuotes = term?.evidenceQuotes.length ?? 0;
          } else {
            const row = evidenceData?.find((r) => r.icd10Code === code);
            if (!row) return { focusedCode: null, focusedQuoteIndex: 0 };
            totalQuotes = countTimelineQuotes(row.timeline);
          }
          const nextIndex = prev.focusedQuoteIndex + 1;
          if (nextIndex >= totalQuotes) {
            return { focusedCode: null, focusedQuoteIndex: 0 };
          }
          return { focusedCode: code, focusedQuoteIndex: nextIndex };
        }
        return { focusedCode: code, focusedQuoteIndex: 0 };
      });
      setScrollTrigger((n) => n + 1);
    },
    [evidenceData, unmatchedLeadTerms],
  );

  const handleTimelineEvidenceClick = useCallback(
    (quote: { text: string; fileIdentifier: string }) => {
      if (!timelinePanel.activeCode || !evidenceData) return;
      const row = evidenceData.find((r) => r.icd10Code === timelinePanel.activeCode);
      if (!row) return;
      const quoteIndex = findQuoteIndex(row.timeline, quote);
      setEvidenceHighlight({
        focusedCode: timelinePanel.activeCode,
        focusedQuoteIndex: quoteIndex >= 0 ? quoteIndex : 0,
      });
      setScrollTrigger((n) => n + 1);
    },
    [timelinePanel.activeCode, evidenceData],
  );

  return {
    timelinePanel,
    allHighlights,
    activeFileIdentifier,
    scrollTrigger,
    evidenceByCode,
    evidenceHighlight,
    handleOpenTimeline,
    handleCloseTimeline,
    handleToggleHighlight,
    handleTimelineEvidenceClick,
  };
}
